/**
 * <tabbed-object-reference>
 * Tab controller for the Tabbed object reference section.
 * - Reflects the active tab in the URL via ?tab=<key> (pushState, no reload).
 * - Syncs on load and on browser back/forward (popstate).
 * - Full keyboard support (arrow keys, Home/End) per the tablist pattern.
 * - Fills file sizes via a HEAD request to the CDN (reads Content-Length),
 *   fired only when a row scrolls into view (IntersectionObserver). Results
 *   cached per URL; fails silently.
 */

// Shared across all sections/instances on the page.
const TOR_SIZE_CACHE = new Map();

function torFormatBytes(bytes) {
  if (!bytes || bytes < 1) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const rounded = unit === 0 || value >= 100 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${units[unit]}`;
}
class TabbedObjectReference extends HTMLElement {
  constructor() {
    super();
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    this.select = this.querySelector('.tabbed-object-reference__select');
    this.onPopState = this.onPopState.bind(this);
    this.sizeObserver =
      'IntersectionObserver' in window
        ? new IntersectionObserver((entries) => this.onSizeIntersect(entries), { rootMargin: '200px' })
        : null;
  }

  connectedCallback() {
    if (!this.tabs.length) return;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.activate(tab.dataset.tabKey, true));
      tab.addEventListener('keydown', (event) => this.onKeydown(event));
    });

    if (this.select) {
      this.select.addEventListener('change', () => this.activate(this.select.value, true));
    }

    window.addEventListener('popstate', this.onPopState);

    const initial = this.keyFromUrl() || this.tabs[0].dataset.tabKey;
    this.activate(initial, false);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
    if (this.sizeObserver) this.sizeObserver.disconnect();
  }

  keyFromUrl() {
    const key = new URLSearchParams(window.location.search).get('tab');
    if (!key) return null;
    return this.tabs.some((t) => t.dataset.tabKey === key) ? key : null;
  }

  onPopState() {
    const key = this.keyFromUrl() || this.tabs[0].dataset.tabKey;
    this.activate(key, false);
  }

  activate(key, pushUrl) {
    const activeTab = this.tabs.find((t) => t.dataset.tabKey === key) || this.tabs[0];

    this.tabs.forEach((tab) => {
      const selected = tab === activeTab;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = selected ? 0 : -1;
    });

    let activePanel = null;
    this.panels.forEach((panel) => {
      const show = panel.dataset.tabKey === activeTab.dataset.tabKey;
      panel.hidden = !show;
      if (show) activePanel = panel;
    });

    if (this.select && this.select.value !== activeTab.dataset.tabKey) {
      this.select.value = activeTab.dataset.tabKey;
    }

    this.hydrateSizes(activePanel);

    if (pushUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', activeTab.dataset.tabKey);
      window.history.pushState({ tab: activeTab.dataset.tabKey }, '', url);
      // Don't yank focus to a tab that's hidden (e.g. mobile dropdown mode).
      if (activeTab.offsetParent !== null) activeTab.focus();
    }
  }

  // Queue the active panel's rows for size lookup. With an observer, each row
  // only fetches once it scrolls into view; without one, fetch immediately.
  hydrateSizes(panel) {
    if (!panel) return;

    panel.querySelectorAll('.tor-item').forEach((link) => {
      const target = link.querySelector('.tor-item__size--auto');
      if (!target || target.dataset.done || link.dataset.sizeQueued) return;

      if (!this.sizeObserver) {
        this.fetchSize(link);
        return;
      }
      link.dataset.sizeQueued = '1';
      this.sizeObserver.observe(link);
    });
  }

  onSizeIntersect(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      this.sizeObserver.unobserve(entry.target);
      this.fetchSize(entry.target);
    });
  }

  fetchSize(link) {
    const target = link.querySelector('.tor-item__size--auto');
    if (!target || target.dataset.done) return;

    const url = link.href;
    if (!url) {
      target.dataset.done = '1';
      return;
    }

    const apply = (text) => {
      target.dataset.done = '1';
      if (text) {
        target.textContent = text;
        target.hidden = false;
      }
    };

    if (TOR_SIZE_CACHE.has(url)) {
      apply(TOR_SIZE_CACHE.get(url));
      return;
    }

    fetch(url, { method: 'HEAD' })
      .then((res) => {
        const len = res.headers.get('Content-Length');
        const text = len ? torFormatBytes(parseInt(len, 10)) : '';
        TOR_SIZE_CACHE.set(url, text);
        apply(text);
      })
      .catch(() => apply(''));
  }

  onKeydown(event) {
    const index = this.tabs.indexOf(event.target);
    if (index === -1) return;

    let next = null;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = this.tabs[(index + 1) % this.tabs.length];
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
        break;
      case 'Home':
        next = this.tabs[0];
        break;
      case 'End':
        next = this.tabs[this.tabs.length - 1];
        break;
      default:
        return;
    }

    event.preventDefault();
    this.activate(next.dataset.tabKey, true);
  }
}

if (!customElements.get('tabbed-object-reference')) {
  customElements.define('tabbed-object-reference', TabbedObjectReference);
}
